/**
 * COMPREHENSIVE CHART EXPORT SOLUTION
 *
 * Exports the entire chart container including:
 * - SVG chart graphics (Recharts output)
 * - HTML legends (custom SmartLegend components)
 * - Titles, labels, and other HTML elements
 * - Proper styling with oklch color support
 *
 * Approach:
 * 1. Convert the entire HTML container to SVG using foreignObject
 * 2. Inline all computed styles (browser resolves oklch to rgb)
 * 3. Convert SVG to PNG via Canvas API
 *
 * Benefits:
 * - Captures complete chart layout
 * - No CSS parsing issues (uses browser's computed styles)
 * - Works with oklch, color(), and any modern CSS
 * - High quality export at 2x resolution
 */

/**
 * Normalise a computed colour for inlining. Fully transparent colours are dropped (the
 * property is not inlined, so the parent's background shows through — previously
 * `rgba(0, 0, 0, 0)` lost its alpha and became solid black, painting every wrapper black
 * in the exported PNG); semi-transparent colours are kept as `rgba(...)`; opaque `rgb(...)`
 * is converted to hex. Anything else is returned unchanged.
 */
export function inlineColorValue(value: string): string | null {
  const match = value.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/i);
  if (!match) return value;
  const alpha = match[4] === undefined ? 1 : parseFloat(match[4]);
  if (alpha <= 0) return null;
  const [r, g, b] = [match[1], match[2], match[3]].map((c) => parseInt(c, 10));
  if (alpha < 1) return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Clone element and inline all computed styles (including nested SVG elements)
 */
function cloneElementWithInlineStyles(element: HTMLElement): HTMLElement {
  const cloned = element.cloneNode(true) as HTMLElement;
  const originalElements = [element, ...Array.from(element.querySelectorAll('*'))];
  const clonedElements = [cloned, ...Array.from(cloned.querySelectorAll('*'))];

  // Copy computed styles to inline styles for each element
  originalElements.forEach((originalEl, index) => {
    const clonedEl = clonedElements[index] as HTMLElement;
    if (!clonedEl) return;

    const computedStyle = window.getComputedStyle(originalEl);

    // For SVG elements, preserve specific SVG properties
    const isSVGElement = originalEl instanceof SVGElement;

    const importantProps = isSVGElement
      ? [
          'fill',
          'stroke',
          'stroke-width',
          'stroke-dasharray',
          'stroke-linecap',
          'font-family',
          'font-size',
          'font-weight',
          'color',
          'opacity',
          'visibility',
          'display',
          'text-anchor',
          'dominant-baseline',
        ]
      : [
          'color',
          'background-color',
          'border-color',
          'border-width',
          'border-style',
          'border-radius',
          'font-family',
          'font-size',
          'font-weight',
          'line-height',
          'padding',
          'margin',
          'width',
          'height',
          'display',
          'flex-direction',
          'justify-content',
          'align-items',
          'gap',
          'opacity',
          'visibility',
        ];

    importantProps.forEach((prop) => {
      const raw = computedStyle.getPropertyValue(prop);
      // Hex for opaque colours, rgba kept for translucent ones, transparent ones skipped.
      const value = raw && raw.startsWith('rgb') ? inlineColorValue(raw) : raw;

      if (value && value !== 'none' && value !== 'normal' && value !== '') {
        clonedEl.style.setProperty(prop, value, 'important');
      }
    });
  });

  return cloned;
}

/**
 * Convert HTML element to SVG using foreignObject
 */
function htmlToSVG(element: HTMLElement): SVGSVGElement {
  // Clone element with inlined styles
  const clonedElement = cloneElementWithInlineStyles(element);

  // Get dimensions
  const bbox = element.getBoundingClientRect();
  const width = bbox.width;
  const height = bbox.height;

  // Create SVG wrapper
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', width.toString());
  svg.setAttribute('height', height.toString());
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  // Create foreignObject to embed HTML
  const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
  foreignObject.setAttribute('width', '100%');
  foreignObject.setAttribute('height', '100%');
  foreignObject.setAttribute('x', '0');
  foreignObject.setAttribute('y', '0');

  // Add cloned element to foreignObject
  foreignObject.appendChild(clonedElement);
  svg.appendChild(foreignObject);

  return svg;
}

/**
 * Convert element (HTML or SVG) to PNG and download
 */
async function elementToPng(element: HTMLElement, filename: string): Promise<void> {
  // Convert HTML container to SVG
  const svg = htmlToSVG(element);

  // Get dimensions
  const bbox = element.getBoundingClientRect();
  const width = bbox.width;
  const height = bbox.height;

  // Serialize SVG to string
  const svgString = new XMLSerializer().serializeToString(svg);
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);

  // Create image from SVG
  const img = new Image();
  img.width = width * 2; // 2x scale for better quality
  img.height = height * 2;

  return new Promise((resolve, reject) => {
    img.onload = () => {
      // Create canvas and draw image
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Fill white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw image
      ctx.drawImage(img, 0, 0, img.width, img.height);

      // Clean up
      URL.revokeObjectURL(svgUrl);

      // Convert to PNG and download
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob'));
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        resolve();
      }, 'image/png');
    };

    img.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      reject(new Error('Failed to load SVG'));
    };

    img.src = svgUrl;
  });
}

/**
 * Sanitise a filename: strips forbidden chars, replaces spaces with underscores, caps at 80 chars.
 */
// Control characters U+0000–U+001F, plus forbidden filename chars
// eslint-disable-next-line no-control-regex
const FORBIDDEN_FILENAME_CHARS = /[\x00-\x1f/\\:*?"<>|]/g;

/**
 * Sanitise a filename: strips forbidden chars, replaces spaces with underscores, caps at 80 chars.
 */
export function sanitiseFilename(name: string): string {
  return name
    .replace(FORBIDDEN_FILENAME_CHARS, '')
    .replace(/ /g, '_')
    .slice(0, 80);
}

export async function exportChartAsPNG(element: HTMLElement, filename: string): Promise<void> {
  try {
    // Export the entire HTML container (including legends, titles, etc.)
    await elementToPng(element, filename);
  } catch (error) {
    console.error('Failed to export chart:', error);
    throw error;
  }
}
